import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Trophy, ArrowLeft, Sparkles, History as HistoryIcon, Package } from 'lucide-react';
import { API } from '../config';
import { MobileNav } from '../components/MobileNav';
import { WinnerFeed } from '../components/WinnerFeed';

const playfairFont = "'Playfair Display', serif";
const fmt = (n) => new Intl.NumberFormat('id-ID').format(Number(n) || 0);

const STATUS_BADGE = {
  active: { bg: 'rgba(34,197,94,0.2)', fg: '#22C55E', label: 'LIVE' },
  paused: { bg: 'rgba(234,179,8,0.2)', fg: '#EAB308', label: 'PAUSED' },
  sold_out: { bg: 'rgba(239,68,68,0.2)', fg: '#EF4444', label: 'SOLD OUT' },
};

const FALLBACK_BG =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 400"><defs><linearGradient id="g" x1="0%25" y1="0%25" x2="100%25" y2="100%25"><stop offset="0%25" stop-color="%23032014"/><stop offset="100%25" stop-color="%23080C0A"/></linearGradient></defs><rect fill="url(%23g)" width="800" height="400"/><text x="400" y="200" fill="%23D4AF37" font-family="serif" font-size="32" text-anchor="middle" dy=".3em" font-style="italic">Kuji</text></svg>';

export const KujiList = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/kuji/events`);
        // Sort featured first, then active before paused/sold_out
        const ranked = (data || []).slice().sort((a, b) => {
          const order = { active: 0, paused: 1, sold_out: 2 };
          return (order[a.status] || 9) - (order[b.status] || 9);
        });
        setEvents(ranked);
      } catch (_e) {
        setEvents([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen pb-32" style={{ background: '#080C0A', fontFamily: "'Outfit', sans-serif" }} data-testid="kuji-list-page">
      <header className="sticky top-0 z-40 backdrop-blur-2xl" style={{ background: 'rgba(8, 12, 10, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between gap-3">
          <button onClick={() => navigate('/')} className="flex items-center gap-2" data-testid="kuji-back-home">
            <ArrowLeft className="w-5 h-5" style={{ color: '#F8FAF9' }} />
            <span style={{ color: '#F8FAF9' }}>Home</span>
          </button>
          <div className="flex items-center gap-2">
            <Trophy className="w-6 h-6" style={{ color: '#D4AF37' }} />
            <h1 className="text-xl md:text-2xl font-light" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Kuji Events</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/inventory')}
              className="flex items-center gap-1 text-sm"
              style={{ color: '#D4AF37' }}
              data-testid="kuji-my-inventory-btn"
            >
              <Package className="w-4 h-4" /> Inventory
            </button>
            <button
              onClick={() => navigate('/kuji/history')}
              className="flex items-center gap-1 text-sm"
              style={{ color: '#D4AF37' }}
              data-testid="kuji-my-history-btn"
            >
              <HistoryIcon className="w-4 h-4" /> History
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        {loading && (
          <div className="text-center py-20">
            <div className="inline-block w-10 h-10 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4AF37', borderTopColor: 'transparent' }} />
          </div>
        )}
        {!loading && events.length === 0 && (
          <div className="text-center py-20 rounded-2xl backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <Sparkles className="w-16 h-16 mx-auto mb-4" style={{ color: '#A3B8AC' }} />
            <p style={{ color: '#A3B8AC' }}>No Kuji events available right now.</p>
            <p className="text-sm mt-1" style={{ color: '#A3B8AC' }}>Check back soon for new launches.</p>
          </div>
        )}
        {!loading && events.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="kuji-events-grid">
            {events.map((ev) => (
              <KujiCard key={ev.id} event={ev} onClick={() => navigate(`/kuji/${ev.slug}`)} />
            ))}
          </div>
        )}

        {/* Realtime Winner Feed (all events) */}
        <div className="mt-12">
          <WinnerFeed variant="compact" limit={30} />
        </div>
      </div>

      <MobileNav />
    </div>
  );
};

const KujiCard = ({ event, onClick }) => {
  const badge = STATUS_BADGE[event.status];
  const remaining = event.remaining_slots || 0;
  const total = event.total_slots || 1;
  const pct = Math.max(0, Math.min(100, (remaining / total) * 100));
  return (
    <motion.div
      whileHover={{ y: -6 }}
      onClick={onClick}
      className="cursor-pointer rounded-2xl overflow-hidden backdrop-blur-xl"
      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
      data-testid={`kuji-card-${event.id}`}
    >
      <div
        className="aspect-video relative"
        style={{
          backgroundImage: `url(${event.banner_url || FALLBACK_BG})`,
          backgroundSize: 'cover', backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(8,12,10,0) 0%, rgba(8,12,10,0.85) 100%)' }} />
        <div className="absolute top-3 left-3 flex gap-2">
          {badge && (
            <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-wider" style={{ background: badge.bg, color: badge.fg }}>
              {badge.label}
            </span>
          )}
          {event.featured && (
            <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-wider" style={{ background: 'rgba(212,175,55,0.2)', color: '#D4AF37' }}>
              FEATURED
            </span>
          )}
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          <h3 className="text-lg font-medium truncate" style={{ color: '#F8FAF9', fontFamily: playfairFont }}>{event.name}</h3>
        </div>
      </div>
      <div className="p-4">
        <div className="flex justify-between items-center mb-2 text-sm">
          <span style={{ color: '#D4AF37' }}>{fmt(event.coin_price_per_draw)} coins / draw</span>
          <span style={{ color: '#A3B8AC' }}>{fmt(remaining)}/{fmt(total)} left</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #D4AF37, #F0C955)' }} />
        </div>
      </div>
    </motion.div>
  );
};
